import React, { useMemo, useCallback, useRef, useEffect, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { AgGrid, AgGridReact, Grid, Flex, Text } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { toDbId } from "#utils/graphql_utils";
import { ClientSelector } from "../shared/ClientSelector";
import { BankAccountConfigurationSlideout } from "./BankAccountConfigurationSlideout";
import { BankAccountsGraphqlDataSource } from "./bankAccountsGQLDataSource";
import { columnDefinitions } from "./columnDefinitions";
import { tableContainer, headerContainer, clientSelectorContainer } from "./styles_vanilla.css";
import { BankAccountRowItem } from "./types";

const rowCountPerPage = 20;

const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
  enableCellTextSelection: true,
  pagination: true,
  paginationPageSize: rowCountPerPage,
  paginationPageSizeSelector: false,
  rowHeight: 50,
  suppressCellFocus: true,
  suppressContextMenu: true,
};

const defaultColumnOptions = {
  editable: false,
  flex: 1,
  minWidth: 100,
  sortable: false,
};

export interface BankAccountConfigurationTableProps {
  title?: string;
  onArenaChange?: (arenaId: number) => void;
}

export const BankAccountConfigurationTable: React.FC<BankAccountConfigurationTableProps> = ({
  title = "Bank account configuration",
  onArenaChange,
}) => {
  const client = useApolloClient();
  const gridApiRef = useRef<AgGrid.GridApi | null>(null);
  const dataSourceRef = useRef<BankAccountsGraphqlDataSource | null>(null);
  const [selectedArenaId, setSelectedArenaId] = useState<number | null>(null);
  const [selectedArenaName, setSelectedArenaName] = useState<string>("");
  const [selectedArenaDomain, setSelectedArenaDomain] = useState<string | undefined>(undefined);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [selectedBankAccount, setSelectedBankAccount] = useState<BankAccountRowItem | null>(
    null,
  );

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    const ds = new BankAccountsGraphqlDataSource(client, rowCountPerPage, selectedArenaId || 0);
    dataSourceRef.current = ds;
    return ds;
  }, [client, selectedArenaId]);

  const handleClientSelect = useCallback(
    (arenaId: string, arenaName: string, arenaDomain?: string) => {
      const numericArenaId = toDbId(arenaId);
      if (!isNaN(numericArenaId)) {
        setSelectedArenaId(numericArenaId);
        setSelectedArenaName(arenaName);
        setSelectedArenaDomain(arenaDomain);
        onArenaChange?.(numericArenaId);
      }
    },
    [onArenaChange],
  );

  useEffect(() => {
    if (dataSourceRef.current && selectedArenaId) {
      dataSourceRef.current.setArenaId(selectedArenaId);
      if (gridApiRef.current) {
        gridApiRef.current.refreshServerSide({ purge: true });
      }
    }
  }, [selectedArenaId]);

  const handleGridReady = useCallback((params: AgGrid.GridReadyEvent) => {
    gridApiRef.current = params.api;
  }, []);

  const handleRowClicked = useCallback((event: AgGrid.RowClickedEvent<BankAccountRowItem>) => {
    if (event.data) {
      setSelectedBankAccount(event.data);
      setIsSlideoutOpen(true);
    }
  }, []);

  const handleSlideoutClose = useCallback(() => {
    setIsSlideoutOpen(false);
    setSelectedBankAccount(null);
  }, []);

  const handleSlideoutSave = useCallback(() => {
    if (dataSourceRef.current) {
      dataSourceRef.current.resetCursor();
    }
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true });
    }
    setIsSlideoutOpen(false);
    setSelectedBankAccount(null);
  }, []);

  const handleEntitySave = useCallback((_entity: { entityId: number; entityName: string }) => {
    if (dataSourceRef.current) {
      dataSourceRef.current.resetCursor();
    }
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true });
    }
  }, []);

  return (
    <Flex flexDirection="column" style={{ height: "100%", width: "100%" }}>
      <Flex className={headerContainer} flexDirection="column" gap={Sizes.MD}>
        <Text variant="h2">{title}</Text>
        <Flex flexDirection="column" gap={Sizes.XS}>
          <Text variant="strong-sm">Client name</Text>
          <Flex className={clientSelectorContainer}>
            <ClientSelector
              selectedArenaName={selectedArenaName}
              onClientSelect={handleClientSelect}
              placeholder="Select a client"
            />
          </Flex>
        </Flex>
      </Flex>
      {selectedArenaId ? (
        <Grid<BankAccountRowItem>
          className={tableContainer}
          cacheBlockSize={rowCountPerPage}
          columnDefs={columnDefinitions}
          defaultColDef={defaultColumnOptions}
          gridId="bank_account_configuration_table"
          gridOptions={gridOptions}
          onGridReady={handleGridReady}
          onRowClicked={handleRowClicked}
          reactiveCustomComponents
          rowModelType="serverSide"
          serverSideDatasource={gridDataSource}
        />
      ) : null}
      <BankAccountConfigurationSlideout
        isOpen={isSlideoutOpen}
        onClose={handleSlideoutClose}
        onSave={handleSlideoutSave}
        onEntitySave={handleEntitySave}
        bankAccount={selectedBankAccount}
        arenaId={selectedArenaId}
        arenaDomain={selectedArenaDomain}
      />
    </Flex>
  );
};

export default BankAccountConfigurationTable;
